import React, { useState } from 'react';

import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Divider from '@amzn/meridian/divider';
import Row from '@amzn/meridian/row';

import { MessageItem } from '../MessageItem';
import { Message } from '../types';

interface IntermediateMessagesCollapseProps {
    messages: Message[];
    conversationId?: string;
    showTechnicalMessages?: boolean;
}

export const IntermediateMessagesCollapse: React.FC<IntermediateMessagesCollapseProps> = ({
    messages,
    conversationId,
    showTechnicalMessages = false
}) => {
    const [isExpanded, setIsExpanded] = useState(false);

    // Count messages based on toggle state for threshold calculation
    const messagesToCount = showTechnicalMessages
        ? messages // Count all messages when toggle is on
        : messages.filter((msg) => msg.messageType === 'system'); // Count only system messages when toggle is off

    // When technical toggle is ON, start expanded but allow manual collapse
    // When technical toggle is OFF, use normal isExpanded state
    const [wasShowingTechnical, setWasShowingTechnical] = useState(showTechnicalMessages);

    // Auto-expand when technical toggle is first turned on
    if (showTechnicalMessages && !wasShowingTechnical) {
        setIsExpanded(true);
        setWasShowingTechnical(true);
    } else if (!showTechnicalMessages && wasShowingTechnical) {
        setWasShowingTechnical(false);
    }

    const effectiveIsExpanded = isExpanded;

    const handleToggleExpand = () => {
        setIsExpanded(!isExpanded);
    };

    // When 5 or fewer messages, show all normally
    if (messagesToCount.length <= 5) {
        return (
            <Column spacing="200">
                {messages.map((message, index) => (
                    <MessageItem
                        key={`${message.timestamp}-${index}`}
                        message={message}
                        conversationId={conversationId}
                    />
                ))}
            </Column>
        );
    }

    // Show first 2 and last 2 messages, collapse the middle ones
    // This layout is used for both technical and non-technical modes
    const firstTwoMessages = messages.slice(0, 2);
    const lastTwoMessages = messages.slice(-2);
    const middleMessages = messages.slice(2, -2);

    return (
        <Column spacing="200">
            {/* First two intermediate messages */}
            {firstTwoMessages.map((message, index) => (
                <MessageItem
                    key={`first-${message.timestamp}-${index}`}
                    message={message}
                    conversationId={conversationId}
                />
            ))}

            {/* Collapsible container */}
            <Row alignmentHorizontal="start">
                <Box type="outline" spacingInset="400" width={effectiveIsExpanded ? '100%' : 'fit'}>
                    <Column spacing="200">
                        {/* Toggle button */}
                        <Row alignmentHorizontal="center">
                            <Button type="link" size="small" onClick={handleToggleExpand}>
                                {effectiveIsExpanded
                                    ? '▲ Hide intermediate messages'
                                    : `▼ Show ${middleMessages.length} more ${middleMessages.length === 1 ? 'message' : 'messages'}`}
                            </Button>
                        </Row>

                        {/* Middle messages (when expanded) */}
                        {effectiveIsExpanded && (
                            <Column spacing="400">
                                {middleMessages.map((message, index) => (
                                    <React.Fragment key={`middle-${message.timestamp}-${index}`}>
                                        {index > 0 && <Divider />}
                                        <MessageItem message={message} conversationId={conversationId} compact={true} />
                                    </React.Fragment>
                                ))}
                            </Column>
                        )}
                    </Column>
                </Box>
            </Row>

            {/* Last two intermediate messages */}
            {lastTwoMessages.map((message, index) => (
                <MessageItem
                    key={`last-${message.timestamp}-${index}`}
                    message={message}
                    conversationId={conversationId}
                />
            ))}
        </Column>
    );
};
